import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCookies } from '../contexts/CookieContext';
import brand from '../config/brand';
import { 
  Calendar, 
  Mail, 
  MessageSquare,
  Cookie
} from 'lucide-react';

const Footer = () => {
  const { t } = useTranslation('public');
  const location = useLocation();
  const { openPreferences } = useCookies();
  const [footerEmail, setFooterEmail] = useState(brand.contactEmail);
  const [teamPageEnabled, setTeamPageEnabled] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const loadFooterSettings = async () => {
      try {
        // Charger email
        const emailResponse = await fetch(`${backendUrl}/api/site-settings/footer-email`);
        if (emailResponse.ok) {
          const data = await emailResponse.json();
          if (data.email) {
            setFooterEmail(data.email);
          }
        }
        // Charger état page équipe
        const teamResponse = await fetch(`${backendUrl}/api/site-settings/team`);
        if (teamResponse.ok) {
          const data = await teamResponse.json();
          setTeamPageEnabled(data.enabled);
        }
      } catch (error) {
        // Utiliser les valeurs par défaut en cas d'erreur
      }
    };
    loadFooterSettings();
  }, [backendUrl]);

  // Masqué dans l'espace admin (footer non pertinent + web app mobile plein écran)
  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  return (
    <>
      <footer className="bg-brand-text text-white mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          
          {/* Main Footer Content */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            
            {/* Nos Solutions */}
            <div>
              <h3 className="text-base sm:text-lg font-bold text-brand-accent mb-4 sm:mb-6">{t('footer.ourSolutions')}</h3>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <Link to="/solutions/particuliers" className="text-sm sm:text-base text-gray-300 hover:text-brand-accent transition-colors">
                    {t('footer.individuals')}
                  </Link>
                </li>
                <li>
                  <Link to="/solutions/professionnels" className="text-sm sm:text-base text-gray-300 hover:text-brand-accent transition-colors">
                    {t('footer.professionals')}
                  </Link>
                </li>
                <li>
                  <Link to="/solutions/expatries" className="text-sm sm:text-base text-gray-300 hover:text-brand-accent transition-colors">
                    {t('footer.expatriates')}
                  </Link>
                </li>
                <li>
                  <Link to="/solutions/voyage" className="text-sm sm:text-base text-gray-300 hover:text-brand-accent transition-colors">
                    {t('footer.travel')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Pages */}
            <div>
              <h3 className="text-base sm:text-lg font-bold text-brand-accent mb-4 sm:mb-6">{t('footer.quickLinks')}</h3>
              <ul className="space-y-2 sm:space-y-3">
                <li><Link to="/" className="text-sm sm:text-base text-gray-300 hover:text-brand-accent transition-colors">{t('footer.home')}</Link></li>
                {teamPageEnabled && (
                  <li><Link to="/about" className="text-sm sm:text-base text-gray-300 hover:text-brand-accent transition-colors">{t('footer.about')}</Link></li>
                )}
                <li><Link to="/contact" className="text-sm sm:text-base text-gray-300 hover:text-brand-accent transition-colors">{t('footer.contact')}</Link></li>
                <li><Link to="/rendez-vous" className="text-sm sm:text-base text-gray-300 hover:text-brand-accent transition-colors">{t('header.appointment')}</Link></li>
              </ul>
            </div>

            {/* Liens Légaux */}
            <div className="col-span-2 sm:col-span-1">
              <h3 className="text-base sm:text-lg font-bold text-brand-accent mb-4 sm:mb-6">{t('footer.legalNotice')}</h3>
              <ul className="grid grid-cols-2 sm:grid-cols-1 gap-2 sm:gap-3">
                <li><Link to="/informations-legales" className="text-sm sm:text-base text-gray-300 hover:text-brand-accent transition-colors">{t('legal.legalNotice.tabs.mentions')}, {t('legal.legalNotice.tabs.cgu')} & {t('legal.legalNotice.tabs.cgv')}</Link></li>
                <li><Link to="/rgpd" className="text-sm sm:text-base text-gray-300 hover:text-brand-accent transition-colors">{t('footer.privacyPolicy')}</Link></li>
                <li><Link to="/cookies" className="text-sm sm:text-base text-gray-300 hover:text-brand-accent transition-colors">{t('footer.cookies')}</Link></li>
              </ul>
            </div>

            {/* Actions */}
            <div className="col-span-2 sm:col-span-1">
              <h3 className="text-base sm:text-lg font-bold text-brand-accent mb-4 sm:mb-6">{t('footer.contactUs')}</h3>
              <div className="space-y-3 sm:space-y-4">
                
                {/* Bouton Prendre RDV */}
                <Link
                  to="/rendez-vous"
                  className="group w-full bg-brand-primary text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-brand-primary-hover transition-all duration-300 hover:shadow-lg flex items-center justify-center text-sm sm:text-base"
                >
                  <Calendar className="mr-2" size={16} />
                  <span>{t('common.makeAppointment')}</span>
                </Link>

                {/* Bouton Contact */}
                <Link
                  to="/contact"
                  className="group w-full border-2 border-brand-accent text-brand-accent px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-brand-accent hover:text-white transition-all duration-300 flex items-center justify-center text-sm sm:text-base"
                >
                  <MessageSquare className="mr-2" size={16} />
                  <span>{t('common.contactUs')}</span>
                </Link>

                {/* Contact Info */}
                <div className="pt-3 sm:pt-4 border-t border-gray-600 space-y-2">
                  <div className="flex items-center text-gray-300">
                    <Mail size={14} className="mr-2 flex-shrink-0" />
                    <span className="text-xs sm:text-sm truncate">{footerEmail}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-600 mt-6 sm:mt-8 pt-4 sm:pt-6 text-center">
            <p className="text-gray-400 text-xs sm:text-sm">
              © {new Date().getFullYear()} {brand.legalName} - {t('footer.allRightsReserved')}
            </p>
            <p className="text-gray-500 text-xs mt-1">
              {t('contact.brokerTitle')} - ORIAS N° {brand.orias}
            </p>
            
            {/* Gestion des cookies */}
            <div className="mt-4 pt-4 border-t border-gray-600">
              <button
                onClick={openPreferences}
                className="inline-flex items-center text-gray-400 hover:text-brand-accent transition-colors text-xs sm:text-sm"
              >
                <Cookie size={14} className="mr-2" />
                {t('footer.cookies')}
              </button>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
